import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const GOLD = "#eec643";

const SCENES = [
  {
    img: "/story/flow.jpg",
    kicker: "42 U.S.C. §654b · §655(a) · §658a",
    headline: ["EVERY SUPPORT DOLLAR", "RIDES A LEDGER."],
    body: "A state disbursement unit collects it. The federal government matches program costs at sixty-six percent. Five performance measures shape the incentives around your family's file.",
  },
  {
    img: "/story/receipts.jpg",
    kicker: "primary sources, open to anyone",
    headline: ["READ THE STATUTES.", "NOT THE RUMOR."],
    body: "The Nevada guideline walkthrough, the federal funding text, and the discovery checklist sit in the open — the actual words, cited, line by line.",
  },
  {
    img: "/story/ledger-room.jpg",
    kicker: "chain 9940 · the ledger room",
    headline: ["THE HOUSE BUILT", "ITS OWN LEDGER."],
    body: "Every entry block-numbered. Every document sealed by its own hash. Every statement re-renderable, forever, against the same record.",
  },
  {
    img: "/story/covenant.jpg",
    kicker: "the family covenant",
    headline: ["TWO PARENTS. ONE CHILD.", "TWO SIGNATURES."],
    body: "A household pool that moves only when both parents sign. Maintenance that clears member to member. A sealed statement on the first of each month.",
  },
  {
    img: "/story/equilibrium.jpg",
    kicker: "the goal, every model",
    headline: ["50/50 IS", "THE DESIGN."],
    body: "Equal time. Equal standing. One child who never visits a parent — they live with both.",
    cta: true,
  },
];

function Scene({ scene, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} data-testid={`story-scene-${index}`}
      className="relative h-[100svh] snap-start overflow-hidden flex items-end">
      <motion.img src={scene.img} alt="" style={{ y }}
        className="absolute inset-0 w-full h-[124%] object-cover" loading={index === 0 ? "eager" : "lazy"} />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/35 to-stone-950/55" />
      <div className="relative w-full max-w-2xl mx-auto px-7 pb-24">
        <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }} viewport={{ once: false, amount: 0.6 }}
          className="font-mono text-[10px] uppercase tracking-[0.35em] mb-4" style={{ color: GOLD }}>
          {scene.kicker}
        </motion.p>
        {scene.headline.map((line, i) => (
          <motion.h2 key={i} initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 + i * 0.14 }} viewport={{ once: false, amount: 0.6 }}
            className="text-4xl sm:text-5xl font-black text-stone-100 leading-[1.02] tracking-tight"
            style={{ fontFamily: "Georgia, serif" }}>
            {line}
          </motion.h2>
        ))}
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }} viewport={{ once: false, amount: 0.6 }}
          className="mt-5 text-sm sm:text-base text-stone-300 leading-relaxed max-w-md">
          {scene.body}
        </motion.p>
        {scene.cta && (
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }} viewport={{ once: false, amount: 0.6 }}
            className="mt-8 flex gap-3 flex-wrap">
            <Link to="/ivd-ledger" data-testid="story-cta-ledger"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-stone-950 transition-transform hover:scale-[1.04] active:scale-95"
              style={{ background: GOLD }}>
              READ THE LEDGER <ArrowUpRight size={16} />
            </Link>
            <Link to="/covenant" data-testid="story-cta-covenant"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-stone-200 border border-stone-500 hover:border-amber-400 transition-colors">
              THE COVENANT WALLET
            </Link>
            <a href="/story/bgnzn-ledger-story.mp4" download data-testid="story-download-reel"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-stone-950 bg-stone-100 hover:bg-white transition-colors">
              DOWNLOAD THE REEL
            </a>
            <a href="/story/bgnzn-ledger-story-short.mp4" download data-testid="story-download-short"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-xs text-stone-200 border border-stone-500 hover:border-amber-400 transition-colors">
              10-SECOND CUT
            </a>
            <a href="/story/diorsdough-how-to-pay.mp4" download data-testid="story-download-diors"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-xs text-stone-200 border border-amber-500/50 hover:border-amber-400 transition-colors">
              DIOR'S HOW-TO-PAY CLIP (15s)
            </a>
            <a href="/story/bgnzn-reply-what-is-bgnzn.mp4" download data-testid="story-download-reply"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-xs text-stone-200 border border-amber-500/50 hover:border-amber-400 transition-colors">
              REPLY CLIP · WHAT IS BGNZN (15s)
            </a>
            <a href="/story/bgnzn-ledger-carousel.zip" download data-testid="story-download-carousel"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-xs text-stone-200 border border-stone-500 hover:border-amber-400 transition-colors">
              FEED CAROUSEL · 8 SLIDES
            </a>
            <a href="/story/bgnzn-caption-pack.txt" download data-testid="story-download-captions"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-xs text-stone-200 border border-stone-500 hover:border-amber-400 transition-colors">
              CAPTION PACK
            </a>
            <a href="/story/bgnzn-invite-card.png" download data-testid="story-download-card"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-xs text-stone-200 border border-stone-500 hover:border-amber-400 transition-colors">
              INVITE QR CARD
            </a>
            <a href="/story/bgnzn-doors-directory.pdf" download data-testid="story-download-directory"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-xs text-stone-950 bg-amber-400 hover:bg-amber-300 transition-colors">
              THE DIRECTORY OF DOORS (PDF)
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
}

export default function Story() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? window.scrollY / max : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div data-testid="story-page" className="bg-stone-950 text-stone-200 h-[100svh] overflow-y-scroll snap-y snap-mandatory">
      <div className="fixed top-0 left-0 right-0 z-20 h-[3px] bg-stone-800">
        <div data-testid="story-progress" className="h-full transition-[width] duration-150" style={{ width: `${progress * 100}%`, background: GOLD }} />
      </div>
      <header className="fixed top-3 left-0 right-0 z-20 flex items-center justify-between px-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-stone-300">bgnzn · the ledger room</span>
        <Link to="/" data-testid="story-exit" className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400 hover:text-amber-400 transition-colors">exit →</Link>
      </header>
      {SCENES.map((s, i) => <Scene key={i} scene={s} index={i} />)}
      <footer className="snap-start bg-stone-950 px-7 py-10 text-center">
        <p className="font-mono text-[9px] text-stone-600 leading-relaxed max-w-md mx-auto">
          Structural education from primary sources. Nothing here is legal advice, a court filing,
          or a claim about any case. The statutes are the text; read them yourself.
        </p>
      </footer>
    </div>
  );
}
